import { useAtom } from 'jotai';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { copyTextToClipboard } from '@/helpers/copy-text-to-clipboard';
import { modalState } from '@/state/modal.state';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Modal } from '@/ui/modal/modal';
import { notify } from '@/ui/snackbar/notify';

export const AccessTokenCreatedModal = (): ReactElement | null => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const { t: tDefault } = useTranslation('default');
  const [modal, setModal] = useAtom(modalState);
  const [hasCopiedToken, setHasCopiedToken] = useState(false);

  if (modal?.state !== 'accessTokenCreated') {
    return null;
  }

  const close = (): void => {
    setHasCopiedToken(false);
    setModal(null);
  };

  const handleCopyRawKey = async (): Promise<void> => {
    try {
      await copyTextToClipboard(modal.rawKey);
      setHasCopiedToken(true);
      notify({ message: t`tokenCreated.copied` });
    } catch {
      notify({ message: tDefault`unknownError` });
    }
  };

  return (
    <Modal
      header={t`tokenCreated.title`}
      mainAction={{
        label: t`tokenCreated.done`,
        onClick: close,
      }}
      minWidth="480px"
      onClose={close}
      secondaryAction={{
        label: t`tokenCreated.copy`,
        onClick: () => {
          void handleCopyRawKey();
        },
      }}
      variant="info"
    >
      <TokenModalBody direction="column" gap="16px" fullWidth>
        <WarningText>{t`tokenCreated.warning`}</WarningText>
        <ReadonlyTokenInput readOnly value={modal.rawKey} />
        {hasCopiedToken ? (
          <MutedText>{t`tokenCreated.copied`}</MutedText>
        ) : null}
      </TokenModalBody>
    </Modal>
  );
};

const TokenModalBody = styled(Flexbox)`
  min-height: 80px;
  margin-bottom: 12px;
`;

const WarningText = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

const ReadonlyTokenInput = styled('textarea')`
  background-color: ${({ theme }) => theme.colors.gray.c2};
  border-radius: 8px;
  border: 0.5px solid ${({ theme }) => theme.colors.gray.c4};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.basics.black};
  font-family: monospace;
  font-size: 13px;
  min-height: 72px;
  padding: 12px;
  resize: vertical;
  width: 100%;
`;

const MutedText = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c10};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 14px;
  margin: 0;
`;
